import { useMemo, useState } from 'react';
import { Loader, TriangleAlert } from 'lucide-react';
import { ESTADOS_ALERTA, TIPOS_ALERTA, TITULO_POR_TIPO } from '@tms/contratos';
import { api } from '../api';
import { BarraApilada, BarrasHorizontales, TarjetaGrafico, type DatoBarra } from '../components/Graficos';
import { SelectorFecha } from '../components/SelectorFecha';
import { ALERTA, PARADA } from '../components/estados';
import { LIMITE_CONDUCCION_H } from '../config/jornada';
import { useDatos } from '../state/Datos';
import { fmtNum, hoyAR } from '../util/format';
import { useEstadoDiarioSemana } from '../util/estadoDiario';
import { useCarga } from '../util/hooks';

export function ReportesPagina() {
  const { alertas } = useDatos();
  const [fecha, setFecha] = useState(hoyAR());
  const semana = useEstadoDiarioSemana(hoyAR());
  const jornadas = useCarga(() => api.jornadas(fecha), [fecha]);
  // El listado ya trae cumplidas, excedidas y omitidas por viaje.
  const viajes = useCarga(async () => {
    const lista = await api.viajes({ tamanio: 200 });
    return lista.items.filter((v) => v.estado === 'EN_CURSO' || v.estado === 'FINALIZADO');
  }, []);

  const kmPorUnidad = useMemo<DatoBarra[]>(() => {
    const suma = new Map<string, { patente: string; km: number }>();
    for (const u of semana.datos?.unidades ?? []) {
      const e = suma.get(u.vehiculoIdMicrotrack) ?? { patente: u.patente ?? u.vehiculoIdMicrotrack, km: 0 };
      e.km += u.km;
      suma.set(u.vehiculoIdMicrotrack, e);
    }
    return [...suma.values()]
      .filter((e) => e.km > 0)
      .sort((a, b) => b.km - a.km)
      .slice(0, 12)
      .map((e) => ({ etiqueta: e.patente, valor: Math.round(e.km) }));
  }, [semana.datos]);

  const horasPorChofer = useMemo<DatoBarra[]>(
    () =>
      [...(jornadas.datos?.jornadas ?? [])]
        .sort((a, b) => b.horasConduccion - a.horasConduccion)
        .map((j) => ({
          etiqueta: j.nombre ?? j.llaveOperador,
          valor: j.horasConduccion,
          color: j.horasConduccion > LIMITE_CONDUCCION_H ? ('amber' as const) : ('brand' as const),
          nota: j.horasConduccion > LIMITE_CONDUCCION_H ? `(> ${LIMITE_CONDUCCION_H} h)` : undefined,
        })),
    [jornadas.datos],
  );

  const porTipo = useMemo<DatoBarra[]>(
    () =>
      TIPOS_ALERTA.map((t) => ({ etiqueta: TITULO_POR_TIPO[t], valor: alertas.filter((a) => a.tipo === t).length }))
        .filter((d) => d.valor > 0)
        .sort((a, b) => b.valor - a.valor),
    [alertas],
  );

  const porEstado = ESTADOS_ALERTA.map((e) => ({ etiqueta: ALERTA[e].etiqueta, valor: alertas.filter((a) => a.estado === e).length, color: ALERTA[e].color, icono: ALERTA[e].icono }));

  const cumplimiento = useMemo(() => {
    const vs = viajes.datos ?? [];
    const suma = (f: (v: (typeof vs)[number]) => number) => vs.reduce((n, v) => n + f(v), 0);
    const total = suma((v) => v.paradasTotal);
    const cumplidas = suma((v) => v.paradasCumplidas);
    const excedidas = suma((v) => v.paradasExcedidas);
    const omitidas = suma((v) => v.paradasOmitidas);
    return { total, cumplidas, excedidas, omitidas, pendientes: Math.max(0, total - cumplidas - excedidas - omitidas) };
  }, [viajes.datos]);
  const exigibles = cumplimiento.total - cumplimiento.pendientes;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">OPERACIONES / INDICADORES</div>
          <h1>Reportes</h1>
        </div>
        <SelectorFecha fecha={fecha} onCambiar={setFecha} />
      </div>

      {(semana.error || jornadas.error || viajes.error) && (
        <div className="banner error" role="alert">
          <TriangleAlert aria-hidden />
          {semana.error ?? jornadas.error ?? viajes.error}
        </div>
      )}

      <div className="reportes">
        <TarjetaGrafico titulo="Kilómetros por unidad" unidad="km, últimos 7 días" vacio={!semana.cargando && kmPorUnidad.length === 0} pie="Las 12 unidades con más kilómetros. Fuente: estado diario de unidades.">
          {semana.cargando && !semana.datos ? <Cargando /> : <BarrasHorizontales datos={kmPorUnidad} sufijo=" km" />}
        </TarjetaGrafico>

        <TarjetaGrafico
          titulo="Horas de conducción por chofer"
          unidad={`horas, ${fecha.split('-').reverse().join('/')}`}
          vacio={!jornadas.cargando && horasPorChofer.length === 0}
          pie={`En ámbar, los choferes que superan ${LIMITE_CONDUCCION_H} h (límite provisorio, a confirmar).`}
        >
          {jornadas.cargando && !jornadas.datos ? <Cargando /> : <BarrasHorizontales datos={horasPorChofer} decimales={1} sufijo=" h" />}
        </TarjetaGrafico>

        <TarjetaGrafico titulo="Alertas por tipo" unidad="cantidad, todas las cargadas" vacio={porTipo.length === 0}>
          <BarrasHorizontales datos={porTipo} />
        </TarjetaGrafico>

        <TarjetaGrafico titulo="Alertas por estado" unidad="cantidad y porcentaje" vacio={alertas.length === 0}>
          <BarraApilada segmentos={porEstado} />
        </TarjetaGrafico>

        <TarjetaGrafico
          titulo="Cumplimiento de paradas"
          unidad="paradas de viajes en curso y finalizados"
          vacio={!viajes.cargando && cumplimiento.total === 0}
          pie={viajes.datos ? `Calculado sobre ${viajes.datos.length} viajes del listado.` : undefined}
        >
          {viajes.cargando && !viajes.datos ? (
            <Cargando />
          ) : (
            <>
              <p style={{ marginBottom: 14 }}>
                <span className="num-grande green">{exigibles ? fmtNum(((cumplimiento.cumplidas + cumplimiento.excedidas) / exigibles) * 100, 0) : '—'} %</span>{' '}
                <span className="muted" style={{ fontSize: 12 }}>
                  cumplidas (incluye excedidas) sobre las paradas ya vencidas ({fmtNum(exigibles)})
                </span>
              </p>
              <BarraApilada
                segmentos={[
                  { etiqueta: PARADA.CUMPLIDA.etiqueta + 's', valor: cumplimiento.cumplidas, color: PARADA.CUMPLIDA.color, icono: PARADA.CUMPLIDA.icono },
                  { etiqueta: PARADA.EXCEDIDA.etiqueta + 's', valor: cumplimiento.excedidas, color: PARADA.EXCEDIDA.color, icono: PARADA.EXCEDIDA.icono },
                  { etiqueta: PARADA.OMITIDA.etiqueta + 's', valor: cumplimiento.omitidas, color: PARADA.OMITIDA.color, icono: PARADA.OMITIDA.icono },
                  { etiqueta: PARADA.PENDIENTE.etiqueta + 's', valor: cumplimiento.pendientes, color: PARADA.PENDIENTE.color, icono: PARADA.PENDIENTE.icono },
                ]}
              />
            </>
          )}
        </TarjetaGrafico>
      </div>
    </div>
  );
}

function Cargando() {
  return (
    <div className="skeleton">
      <Loader className="spin" aria-hidden size={16} />
      Cargando…
    </div>
  );
}
